import React, { useState, useEffect } from "react";
import { Button, Col, Container, Form, Row } from "react-bootstrap";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useForm, useFieldArray } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { catchAsync, checkResponse } from "../../../../utilities/utilities";
import {
  ADDAPPINTRO,
  APPINTRODETAIL,
  UPDATEAPPINTRO,
} from "../../../../services/ApiCalls";
import Loader from "../../../../components/Common/Loader";
import { useTranslation } from "react-i18next";
import uploadMultipleFiles from "../../../../utilities/uploadMultipleFiles";

// Dropdown option sources
const TYPE_OPTIONS = ["customer", "subadmin"];
const LANG_OPTIONS = ["English", "Vietnamese", "Thai"];
const STATUS_OPTIONS = ["active", "inactive", "archived"];

// Media constraints
const MIN_MEDIA = 1; // unchanged from before — only the max was raised
const MAX_MEDIA = 5;

const appIntroSchema = z.object({
  title: z.string().min(1, "Title is required"),
  description: z.string().min(1, "Description is required"),
  type: z.enum(TYPE_OPTIONS, { required_error: "Type is required" }),
  lang: z.enum(LANG_OPTIONS, { required_error: "Language is required" }),
  order: z
    .coerce.number({ invalid_type_error: "Order must be a number" })
    .min(0, "Order is required"),
  status: z.enum(STATUS_OPTIONS, { required_error: "Status is required" }),
  backgroundColor: z.string().min(1, "Background color is required"),
  media: z
    .array(
      z.object({
        fileId: z.string(),
        order: z.coerce.number({ invalid_type_error: "Order must be a number" }),
        // "url" is intentionally NOT declared here — it's only used to
        // render the thumbnail. Zod strips unknown keys by default, so it
        // is automatically dropped from the submitted payload.
      })
    )
    .min(MIN_MEDIA, `At least ${MIN_MEDIA} images are required`)
    .max(MAX_MEDIA, `At max ${MAX_MEDIA} images are allowed`),
});

const AddAppIntro = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const page = queryParams.get("page") || 1;
  const [introData, setIntroData] = useState(null);
  const [imgloader, setImgLoader] = useState(false);
  const [loader, setLoader] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(appIntroSchema),
    defaultValues: {
      title: "",
      description: "",
      type: "customer",
      lang: "English",
      order: 0,
      status: "active",
      backgroundColor: "#F5F7FA",
      media: [],
    },
    values: {
      title: introData?.title ?? "",
      description: introData?.description ?? "",
      type: introData?.type ?? "customer",
      lang: introData?.lang ?? "English",
      order: introData?.order ?? 0,
      status: introData?.status ?? "active",
      backgroundColor: introData?.backgroundColor ?? "#F5F7FA",
      // NOTE: assumes each media item from the API looks like
      // { fileId: { _id, link }, order }. Adjust the accessors below
      // if your API returns a different shape (e.g. flat { _id, link, order }).
      media:
        introData?.media?.map((ele, idx) => ({
          fileId: ele?.fileId?._id || ele?.fileId || ele?._id,
          order: ele?.order ?? idx,
          url: ele?.fileId?.link || ele?.link,
        })) ?? [],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "media",
  });

  const handleImageChange = catchAsync(async (e) => {
    const remainingSlots = MAX_MEDIA - fields.length;
    if (remainingSlots <= 0) {
      e.target.value = "";
      return;
    }

    setImgLoader(true);

    const files = Array.from(e.target.files).slice(0, remainingSlots);
    if (!files.length) {
      setImgLoader(false);
      e.target.value = "";
      return;
    }

    // call multi-file uploader ONCE
    const res = await uploadMultipleFiles(files);

    if (Array.isArray(res)) {
      res
        .filter((item) => item?._id)
        .forEach((item, idx) => {
          append({
            fileId: item._id,
            // Sensible default so the field isn't empty — the user can
            // freely edit this afterwards via the "Order" input below.
            order: fields.length + idx,
            url: item.link,
          });
        });
    }

    setImgLoader(false);
    e.target.value = ""; // allow re-selecting the same file(s) later
  });

  const submitHandler = catchAsync(async (data) => {
    const body = {
      ...data,
      order: Number(data.order),
      ...(id && { _id: id }),
    };
    const res = await (id ? UPDATEAPPINTRO(body) : ADDAPPINTRO(body));
    const success = checkResponse({ res, showSuccess: true });
    if (success) navigate(`/appintro?page=${page}`);
  });

  const handleCancel = () => navigate(`/appintro?page=${page}`);

  const fetchIntro = async () => {
    setLoader(true);

    try {
      const res = await APPINTRODETAIL(id);
      checkResponse({ res, showSuccess: true, setLoader });
      setIntroData(res?.data?.data);
    } catch (err) {}
    setLoader(false);
  };

  useEffect(() => {
    if (id) {
      fetchIntro();
    }
  }, []);

  return (
    <>
      {loader && <Loader />}
      <section className="addRoom position-relative py-3">
        <Container>
          <Row>
            <Col lg="12">
              <div className="d-flex align-items-center gap-10">
                <Link
                  to={`/appintro?page=${page}`}
                  className="border d-flex align-items-center p-2 rounded"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="12"
                    height="12"
                    viewBox="0 0 10 16"
                    fill="none"
                  >
                    <path
                      d="M8.64707 0.473344C8.55514 0.381188 8.44594 0.308072 8.32572 0.258184C8.20549 0.208296 8.07661 0.182617 7.94644 0.182617C7.81628 0.182617 7.68739 0.208296 7.56717 0.258184C7.44694 0.308072 7.33774 0.381188 7.24582 0.473344L0.667065 7.05209C0.593675 7.12533 0.53545 7.21233 0.495723 7.3081C0.455996 7.40387 0.435547 7.50654 0.435547 7.61022C0.435547 7.7139 0.455996 7.81657 0.495723 7.91234C0.53545 8.00811 0.593675 8.0951 0.667065 8.16834L7.24582 14.7471C7.63373 15.135 8.25915 15.135 8.64707 14.7471C9.03498 14.3592 9.03498 13.7338 8.64707 13.3458L2.9154 7.60626L8.65498 1.86668C9.03498 1.48668 9.03498 0.853344 8.64707 0.473344Z"
                      fill="#E8307A"
                      stroke="#E8307A"
                      strokeWidth="0.2"
                    />
                  </svg>
                </Link>
                <h4 className="mb-0 py-3 fw-bold text-dark text-capitalize">
                  {id ? t("appIntro.editAppIntro") : t("appIntro.addAppIntro")}
                </h4>
              </div>
            </Col>

            <Col lg="12" className="my-2">
              <div
                className="formWrpper px-lg-5 p-md-4 p-3 rounded"
                style={{ background: "#EEEEEE" }}
              >
                <Form onSubmit={handleSubmit(submitHandler)}>
                  <Row className="justify-content-between">
                    <Col lg="12" className="my-2">
                      <label>{t("appIntro.columns.title", "Title")}</label>
                      <input className="form-control" {...register("title")} />
                      {errors.title && (
                        <p className="text-danger">{errors.title.message}</p>
                      )}
                    </Col>

                    <Col lg="12" className="my-2">
                      <label>{t("appIntro.columns.description")}</label>
                      <textarea
                        rows={4}
                        className="form-control"
                        {...register("description")}
                      />
                      {errors.description && (
                        <p className="text-danger">
                          {errors.description.message}
                        </p>
                      )}
                    </Col>

                    <Col lg="6" className="my-2">
                      <label>{t("appIntro.columns.type", "Type")}</label>
                      <Form.Select {...register("type")}>
                        {TYPE_OPTIONS.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt.charAt(0).toUpperCase() + opt.slice(1)}
                          </option>
                        ))}
                      </Form.Select>
                      {errors.type && (
                        <p className="text-danger">{errors.type.message}</p>
                      )}
                    </Col>

                    <Col lg="6" className="my-2">
                      <label>{t("appIntro.columns.lang", "Language")}</label>
                      <Form.Select {...register("lang")}>
                        {LANG_OPTIONS.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </Form.Select>
                      {errors.lang && (
                        <p className="text-danger">{errors.lang.message}</p>
                      )}
                    </Col>

                    <Col lg="6" className="my-2">
                      <label>{t("appIntro.columns.status", "Status")}</label>
                      <Form.Select {...register("status")}>
                        {STATUS_OPTIONS.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt.charAt(0).toUpperCase() + opt.slice(1)}
                          </option>
                        ))}
                      </Form.Select>
                      {errors.status && (
                        <p className="text-danger">{errors.status.message}</p>
                      )}
                    </Col>

                    <Col lg="6" className="my-2">
                      <label>{t("appIntro.columns.order", "Order")}</label>
                      <input
                        type="number"
                        min="0"
                        className="form-control"
                        {...register("order")}
                      />
                      {errors.order && (
                        <p className="text-danger">{errors.order.message}</p>
                      )}
                    </Col>

                    <Col lg="12" className="my-2">
                      <label>
                        {t(
                          "appIntro.columns.backgroundColor",
                          "Background Color"
                        )}
                      </label>
                      <div className="d-flex align-items-center gap-2">
                        <input
                          type="color"
                          className="form-control form-control-color"
                          {...register("backgroundColor")}
                        />
                      </div>
                      {errors.backgroundColor && (
                        <p className="text-danger">
                          {errors.backgroundColor.message}
                        </p>
                      )}
                    </Col>

                    <Col lg="12" className="my-2">
                      <div className="d-flex align-items-center justify-content-between">
                        <label className="text-muted ps-2 m-0">
                          {t("appIntro.columns.media", "Upload Images")} (
                          {fields.length}/{MAX_MEDIA})
                        </label>
                      </div>

                      {fields.length < MAX_MEDIA && (
                        <div
                          className="border border-2 border-dashed rounded d-flex flex-column align-items-center justify-content-center p-4 bg-light"
                          style={{ cursor: "pointer", minHeight: "140px" }}
                          onClick={() =>
                            document.getElementById("imageUploadInput").click()
                          }
                        >
                          <i className="bi bi-cloud-upload fs-1 text-muted"></i>
                          <p className="m-0 text-muted">
                            Click or Drag & Drop to Upload
                          </p>
                          <input
                            id="imageUploadInput"
                            type="file"
                            accept="image/*"
                            multiple
                            className="d-none"
                            onChange={handleImageChange}
                          />
                        </div>
                      )}

                      <div className="d-flex flex-wrap gap-3 mt-3">
                        {fields.map((field, index) => (
                          <div
                            key={field.id}
                            style={{ width: 140 }}
                            className="border rounded p-2 shadow-sm"
                          >
                            <div
                              style={{
                                width: "100%",
                                height: 100,
                                position: "relative",
                              }}
                            >
                              <img
                                src={field.url}
                                alt={`preview-${index}`}
                                className="w-100 h-100 object-fit-cover rounded"
                              />
                              <button
                                type="button"
                                className="btn btn-sm btn-danger position-absolute top-0 end-0 m-1 rounded-circle"
                                onClick={() => remove(index)}
                              >
                                ✕
                              </button>
                            </div>
                            <label className="d-block mt-2 mb-0 small text-muted">
                              {t("appIntro.columns.mediaOrder", "Order")}
                            </label>
                            <input
                              type="number"
                              min="0"
                              className="form-control form-control-sm"
                              {...register(`media.${index}.order`, {
                                valueAsNumber: true,
                              })}
                            />
                            {errors.media?.[index]?.order && (
                              <p className="text-danger small mb-0">
                                {errors.media[index].order.message}
                              </p>
                            )}
                          </div>
                        ))}

                        {imgloader && (
                          <div
                            style={{ height: 140, width: 140 }}
                            className="d-flex align-items-center justify-content-center border rounded"
                          >
                            Loading...
                          </div>
                        )}
                      </div>

                      {errors.media && !Array.isArray(errors.media) && (
                        <p className="text-danger mt-2">
                          {errors.media.message}
                        </p>
                      )}
                    </Col>

                    <Col lg="12" className="my-2">
                      <div className="d-flex align-items-center justify-content-center gap-10">
                        <Button
                          type="button"
                          onClick={handleCancel}
                          className="commonBtn GreyBtn"
                        >
                          Cancel
                        </Button>
                        <Button type="submit" className="commonBtn">
                          Submit
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </Form>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
};

export default AddAppIntro;